import type { Host } from '#dn-types/host.ts';

export type HostKind = 'host' | 'lighthouse' | 'relay';

export const HOST_KINDS = ['host', 'lighthouse', 'relay'] as const satisfies readonly HostKind[];

/** Capitalized singular display labels, e.g. for the type filter and its pill. */
export const HOST_KIND_LABELS: Record<HostKind, string> = {
  host: 'Host',
  lighthouse: 'Lighthouse',
  relay: 'Relay',
};

/** Narrow untrusted input (e.g. a URL param) to a {@link HostKind}. */
export function isHostKind(value: string): value is HostKind {
  return (HOST_KINDS as readonly string[]).includes(value);
}

/** Derive the display kind of a host from its type flags. Lighthouse and relay are mutually exclusive. */
export function hostKind(host: Pick<Host, 'isLighthouse' | 'isRelay'>): HostKind {
  if (host.isLighthouse) return 'lighthouse';
  if (host.isRelay) return 'relay';
  return 'host';
}

/** A host is at most one flavor of infrastructure, so these flags can never both be true. */
export type HostFlavorFlags =
  | { isLighthouse: true; isRelay: false }
  | { isLighthouse: false; isRelay: true }
  | { isLighthouse: false; isRelay: false };

/** The inverse of {@link hostKind} — encode a kind as the flags the api stores it under. */
export function hostFlavorFlags(kind: HostKind): HostFlavorFlags {
  if (kind === 'lighthouse') return { isLighthouse: true, isRelay: false };
  if (kind === 'relay') return { isLighthouse: false, isRelay: true };
  return { isLighthouse: false, isRelay: false };
}

/** A host acting as network infrastructure — a lighthouse or relay, never a plain host. */
export type InfraHost = Host & ({ isLighthouse: true } | { isRelay: true });

export function isInfraHost(host: Host): host is InfraHost {
  return host.isLighthouse || host.isRelay;
}
